import { useMemo } from "react";
import { Edge, Node, NodeTypes } from "@xyflow/react";
import { DiagramNode, Graph } from "./Graph";
import { WorkflowGraphLegend } from "./Legend/Legend";
import { InputNode } from "./Nodes/InputNode";
import { ResultNode } from "./Nodes/ResultNode";
import { StepNode } from "./Nodes/StepNode";

type OutputPolicy = "KEEP" | "DISCARD";

type GraphNode<T extends Record<string, string>> = Pick<Node<T>, "id" | "data">;

export type NodeDataBase = { label: string; subLabel?: string };
type ResultData = NodeDataBase & { outputPolicy: OutputPolicy };

const nodeTypes = {
  workflowInput: InputNode,
  workflowStep: StepNode,
  workflowResult: ResultNode,
} satisfies NodeTypes;

type WorkflowNodeType = keyof typeof nodeTypes;

export interface WorkflowDiagramReactFlowProps {
  graphNodes: GraphNode<NodeDataBase>[];
  graphInputs: GraphNode<{ label: string }>[];
  graphResults: GraphNode<ResultData>[];
  graphEdges: Pick<Edge, "id" | "source" | "target">[];
}

export const WorkflowDiagramReactFlow = ({
  graphNodes,
  graphInputs,
  graphResults,
  graphEdges,
}: WorkflowDiagramReactFlowProps) => {
  const nodes = useMemo<DiagramNode<WorkflowNodeType>[]>(
    () => [
      ...graphInputs.map((i) => ({
        id: i.id,
        type: "workflowInput" as const,
        data: i.data,
      })),
      ...graphNodes.map((n) => ({
        id: n.id,
        type: "workflowStep" as const,
        data: n.data,
      })),
      ...graphResults.map((r) => ({
        id: r.id,
        type: "workflowResult" as const,
        data: { ...r.data, outputPolicy: r.data.outputPolicy ?? "KEEP" },
      })),
    ],
    [graphInputs, graphNodes, graphResults],
  );

  return (
    <Graph
      nodeTypes={nodeTypes}
      nodes={nodes}
      edges={graphEdges}
      legend={
        <WorkflowGraphLegend
          hasInputs={graphInputs.length > 0}
          hasSteps={graphNodes.length > 0}
          hasKeptOutputs={graphResults.some(
            (r) => (r.data.outputPolicy ?? "KEEP") === "KEEP",
          )}
          hasDiscardedOutputs={graphResults.some(
            (r) => r.data.outputPolicy === "DISCARD",
          )}
          hasEdges={graphEdges.length > 0}
        />
      }
    />
  );
};
